import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Typography, useTheme } from '@mui/material';
import { Icon } from '../icons';
import { hexToRGBA } from '../../utils/hex-to-rgba';
import { useTranslation } from 'react-i18next';

import { useAuth } from "../../hooks/useAuth";

import './SidebarStyles.css'

const ApsSidebar = () => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.auth.user);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { group_code, project_code } = useParams();
  const [queryParams, setQueryParams] = useState({});
  const [hasImage, setHasImage] = useState(false);
  const [toggled, setToggled] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  
  const auth = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setQueryParams({
      projectName: params.get("projectName"),
      groupName: params.get("groupName"),
      projectId: params.get("pid"),
    });
  }, [location.search]);

  // 메뉴 클릭 핸들러
  const handleMenuClick = (path) => {
    const url = `/aps/${group_code}/${project_code}/${path}`;
    navigate(url);
  };

  // 서브메뉴 클릭 핸들러
  const handleSubMenuClick = (categoryPath, itemPath) => {
    const url = `/aps/${group_code}/${project_code}/${categoryPath}/${itemPath}`;
    navigate(url);
  };

  // 현재 경로에서 카테고리 추출
  const getCurrentCategory = () => {
    const path = location.pathname;
    
    if (path.includes('/plan')) {
      return 'plan';
    } else if (path.includes('/gantt')) {
      return 'gantt';
    } else if (path.includes('/performance')) {
      return 'performance';
    } else if (path.includes('/rule')) {
      return 'rule';
    } else if (path.includes('/standard/')) {
      return 'standard';
    } else {
      return null; // 기본값
    }
  };

  // 현재 선택된 카테고리
  const currentCategory = getCurrentCategory();

  // 현재 경로에서 서브메뉴 추출 (standard 카테고리의 경우)
  const getCurrentSubMenu = () => {
    const path = location.pathname;
    if (path.includes('/standard/')) {
      const lastSegment = path.split('/').pop();
      return lastSegment;
    }
    return null;
  };

  // 현재 선택된 서브메뉴
  const currentSubMenu = getCurrentSubMenu();

  // APS 사이드바 메뉴 데이터 정의
  const apsSidebarMenus = [
    {
      label: t('sidebars.aps.aiProductionPlan'),
      path: 'plan',
      icon: 'todoIcon',
      directLink: true
    },
    {
      label: t('sidebars.aps.ganttChart'),
      path: 'gantt',
      icon: 'ganttIcon',
      directLink: true
    },
    {
      label: t('sidebars.aps.productionPerformance'),
      path: 'performance',
      icon: 'performanceIcon',
      directLink: true
    },
    {
      label: t('sidebars.aps.planningRules'),
      path: 'rule',
      icon: 'shieldIcon',
      directLink: true
    },
    {
      label: t('sidebars.aps.masterData'),
      path: 'standard',
      icon: 'databaseIcon',
      items: [
        { name: t('sidebars.aps.machineManagement'), path: 'machine' },
        { name: t('sidebars.aps.itemManagement'), path: 'item' },
        { name: t('sidebars.aps.operatingTimeManagement'), path: 'time' }
      ]
    }
  ];

  const sidebarTheme = theme.palette.mode === 'light' ? {
    sidebar: {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.default,
      icon: theme.palette.primary.main,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  } : {
    sidebar: {
      backgroundColor: theme.palette.background.default,
      color: theme.palette.text.primary,
    },
    menu: {
      menuContent: theme.palette.background.paper,
      icon: theme.palette.primary.light,
      hover: {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
      disabled: {
        color: theme.palette.action.disabled,
      },
    },
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: sidebarTheme.menu.icon,
      '& svg': {
        width: '20px',
        height: '20px',
      },
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRGBA(sidebarTheme.menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: sidebarTheme.menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRGBA(sidebarTheme.menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: sidebarTheme.menu.hover.color,
      },
    },
    label: {
      opacity: 0.9,
    },
  };

  if (!user) {
    return null;
  }

  return (
    <Sidebar
      collapsed={collapsed}
      toggled={toggled}
      onBackdropClick={() => setToggled(false)}
      backgroundColor={hexToRGBA(sidebarTheme.sidebar.backgroundColor, hasImage ? 0.9 : 1)}
      rootStyles={{
        height: '100%',
        color: sidebarTheme.sidebar.color,
        width: '240px',
        minWidth: '240px',
        borderRight: `1px solid ${theme.palette.divider}`,
        borderLeft: 'none',
        borderBlock: 'none'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <Menu menuItemStyles={menuItemStyles}>
            {apsSidebarMenus.map((menu) => 
              menu.directLink ? (
                // 직접 링크가 있는 메뉴 항목
                <MenuItem
                  key={menu.path}
                  icon={
                    <Icon
                      name={menu.icon}
                      style={{
                        color: currentCategory === menu.path ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)',
                        width: '18px',
                        height: '18px'
                      }} />
                  }
                  onClick={() => handleMenuClick(menu.path)}
                  active={currentCategory === menu.path}
                  style={{
                    color: currentCategory === menu.path ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)',
                    fontWeight: 500,
                    fontSize: '15px',
                    paddingBlock: '12px',
                    backgroundColor: currentCategory === menu.path ? theme.palette.primary.lightOpacity : null,
                  }}
                >
                  {menu.label}
                </MenuItem>
              ) : (
                // 서브메뉴가 있는 메뉴 항목
                <SubMenu
                  key={menu.path}
                  label={menu.label}
                  icon={
                    <Icon
                      name={menu.icon}
                      style={{
                        color: currentCategory === menu.path ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)',
                        width: '18px',
                        height: '18px'
                      }} />
                  }
                  defaultOpen={currentCategory === menu.path}
                  style={{
                    color: currentCategory === menu.path ? theme.palette.primary.main : 'rgba(124, 124, 124, 1)',
                    fontWeight: 500,
                    fontSize: '15px',
                  }}
                >
                  {menu.items.map((item) => (
                    <MenuItem 
                      key={`${menu.path}-${item.path}`}
                      onClick={() => handleSubMenuClick(menu.path, item.path)}
                      active={currentCategory === menu.path && currentSubMenu === item.path}
                      style={{
                        height: 'auto',
                        borderRadius: '6px',
                        marginBlock: '2px',
                        marginInline: '10px',
                        paddingBlock: '8px',
                        paddingInline: '40px',
                        backgroundColor: location.pathname === `/aps/${group_code}/${project_code}/${menu.path}/${item.path}` ? theme.palette.primary.lightOpacity : null,
                      }}
                    >
                      <Typography 
                        variant="caption" 
                        style={{ 
                          fontSize: '14px', 
                          fontWeight: 500, 
                          opacity: 0.9,
                          color: currentCategory === menu.path && currentSubMenu === item.path 
                            ? theme.palette.primary.main 
                            : 'rgba(124, 124, 124, 1)'
                        }}>
                        {item.name}
                      </Typography>
                    </MenuItem>
                  ))}
                </SubMenu>
              )
            )}
          </Menu>
        </div>
      </div>
    </Sidebar>
  );
};

export default ApsSidebar; 